import { jwtDecode } from 'jwt-decode';
import {
  FormatNumberOptions,
  IntlProvider as ReactIntlProvider,
  useIntl as useReactIntl,
} from 'react-intl';
import { useAuth } from 'context/AuthContext/AuthContext';

interface IntlProviderProps {
  children: React.ReactNode;
  defaultLocale: string;
}

export function IntlProvider({
  children,
  defaultLocale,
  ...props
}: IntlProviderProps) {
  const { tokens } = useAuth();
  const userLocale = localeFromId(tokens?.id_token);

  return (
    <ReactIntlProvider
      locale={userLocale || defaultLocale}
      defaultLocale={defaultLocale}
      {...props}
    >
      {children}
    </ReactIntlProvider>
  );
}

/*
 * Wrapper around useIntl hook from 'react-intl' that adds some convenient defaults.
 */
export function useIntl() {
  const intl = useReactIntl();
  const defaultCurrency = getDefaultCurrency(intl.locale);

  function formatCurrency(
    value: number = 0,
    options: FormatNumberOptions = {}
  ) {
    return intl.formatNumber(value / 100, {
      style: 'currency',
      currency: defaultCurrency,
      currencyDisplay: 'narrowSymbol',
      ...options,
    });
  }

  return {
    ...intl,
    formatCurrency,
    defaultCurrency,
  };
}

function getDefaultCurrency(locale: string) {
  if (locale === 'en-GB') return 'GBP';
  return 'AUD';
}

export function localeFromId(token?: string): string | undefined {
  if (!token) return;
  try {
    return jwtDecode<{ locale?: string }>(token).locale;
  } catch (err) {
    console.log(`Error retrieving locale from ID. ${err}`);
  }
}
